import type { Transmission } from "@/services/transmissionsService";

/**
 * Curated starter transmissions shown to anonymous visitors.
 * Negative IDs prevent collision with real DB records.
 *
 * COVERS: these are the same curated URLs the catalogue holds for these books
 * (Apple artwork, or an Open Library -L id). They are NOT Google Books
 * `books/content?id=…` links, which is what they used to be — and four of those
 * five volume ids served one shared "image not available" graphic, byte-identical
 * across Neuromancer, The Left Hand of Darkness, Do Androids and The
 * Dispossessed (md5 a64fa89d…). The cards still looked right only because the
 * cover component silently recovered a replacement for each one.
 *
 * This is the first thing an anonymous visitor sees, so it should not depend on
 * a recovery path at all. If you change a cover here, fetch it first and check
 * you got artwork rather than a placeholder — a placeholder is served with
 * HTTP 200, so a status check alone will not tell you.
 */
export const STARTER_TRANSMISSIONS: Transmission[] = [
  {
    id: -1,
    title: "Neuromancer",
    author: "William Gibson",
    status: "want-to-read",
    tags: ["cyberpunk", "AI", "virtual reality"],
    notes: "",
    cover_url: "https://is1-ssl.mzstatic.com/image/thumb/Publication211/v4/53/69/a1/5369a131-9e29-deb0-ec04-30996b21e829/9781473217393.jpg/600x600bb.jpg",
    rating: {},
    user_id: "",
    created_at: new Date().toISOString(),
    publication_year: 1984,
  },
  {
    id: -2,
    title: "The Left Hand of Darkness",
    author: "Ursula K. Le Guin",
    status: "want-to-read",
    tags: ["gender", "anthropology", "political SF"],
    notes: "",
    cover_url: "https://is1-ssl.mzstatic.com/image/thumb/Publication116/v4/90/93/74/909374d8-99d0-0fca-7f3e-e57e5b0b7ccd/9781473221635.jpg/600x600bb.jpg",
    rating: {},
    user_id: "",
    created_at: new Date().toISOString(),
    publication_year: 1969,
  },
  {
    id: -3,
    title: "Do Androids Dream of Electric Sheep?",
    author: "Philip K. Dick",
    status: "want-to-read",
    tags: ["identity", "dystopia", "empathy"],
    notes: "",
    cover_url: "https://is1-ssl.mzstatic.com/image/thumb/Publication126/v4/d9/cb/73/d9cb73bb-9973-c5c6-9423-c16faad9f698/9780575097933.jpg/600x600bb.jpg",
    rating: {},
    user_id: "",
    created_at: new Date().toISOString(),
    publication_year: 1968,
  },
  {
    id: -4,
    title: "Dune",
    author: "Frank Herbert",
    status: "want-to-read",
    tags: ["ecology", "politics", "messianic"],
    notes: "",
    cover_url: "https://is1-ssl.mzstatic.com/image/thumb/Publication122/v4/32/01/3b/32013b57-7d57-61f6-e8be-2c07b4cdc10b/9780575104419.jpg/600x600bb.jpg",
    rating: {},
    user_id: "",
    created_at: new Date().toISOString(),
    publication_year: 1965,
  },
  {
    id: -5,
    title: "The Dispossessed",
    author: "Ursula K. Le Guin",
    status: "want-to-read",
    tags: ["anarchism", "utopia", "physics"],
    notes: "",
    cover_url: "https://is1-ssl.mzstatic.com/image/thumb/Publication122/v4/57/88/2e/57882e52-657e-a99c-ae8a-0331fa5e83fd/9781473206069.jpg/600x600bb.jpg",
    rating: {},
    user_id: "",
    created_at: new Date().toISOString(),
    publication_year: 1974,
  },
];

/** Check if a transmission is a starter (not from DB) */
/** Starter books are placeholders shown to signed-out readers; they have negative
 *  ids and no database row. By id, for callers that only hold an id. */
export const isStarterId = (id: number) => id < 0;
export const isStarterTransmission = (t: Transmission) => isStarterId(t.id);
